// File Upload → "Tables" tab (flag upload_table_structure_view). Read-only view of the active
// project's tables → fields from GET /upload/tables, in two sub-views:
//  - Structure: one tree grid (tables = parent rows with a +/- toggle, fields = indented child rows).
//  - Relationships: fields with the same clean name in 2+ tables (join candidates the AI can use;
//    matches by name, NOT enforced links).
// Types are decided by the backend (the stored upload classification); everything here is display
// shaping in pure helpers (utils/tableStructure.js), computed from the one response. No LLM.
import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { ChevronsDownUp, ChevronsUpDown, Link2, Minus, Plus, Search, Share2, Table2, X } from "lucide-react";
import { getFreshToken } from "../api";
import { friendlyFileName } from "../utils/fileName";
import {
  buildTreeRows, bulkToggleState, commonFieldReason, computeSharedFields, fieldHeaderDisplay,
  filterSharedFields, filterTableStructure, highlightParts, isSharedMetric, normalizeTableStructure,
  partitionSharedFields, plural, sharedFieldIndex, summarizeTables, summaryText, typeBadge,
  typeSummaryText,
} from "../utils/tableStructure";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");
const SEARCH_ID = "s3upload-tree-search";
const SEARCH_STATUS_ID = "s3upload-tree-search-status";
const GRID_COLS = 6;
const REL_INTRO = "Fields with the same name in 2+ tables. Boomerang uses these to relate tables "
  + "when you ask questions. Matches by name, not enforced links.";

const relRowId = (name) => `s3upload-rel-${String(name).replace(/[^A-Za-z0-9_-]/g, "_")}`;

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// The stored format (e.g. percent_scaled) is detail, not a type: it lives in the tooltip (and for
// screen readers) so it never crowds or clips the compact Type column.
function TypeBadge({ type, format }) {
  const b = typeBadge(type);
  const fmt = format && format !== type ? format : "";
  return (
    <span className="s3upload-type-wrap" title={fmt ? `${b.label} (format: ${fmt})` : undefined}>
      <span className={b.className}>{b.label}</span>
      {fmt && <span className="s3upload-sr-only">, format {fmt}</span>}
    </span>
  );
}

// Wraps every search match in <mark>; plain text when there's no query.
function Highlight({ text, query }) {
  return highlightParts(text, query).map((p, i) =>
    p.match ? <mark key={i} className="s3upload-tree-hl">{p.text}</mark> : <React.Fragment key={i}>{p.text}</React.Fragment>);
}

// Original header column: the file's header(s) that became this clean name. A muted dash when the
// header is identical to the clean name (or there is none); >2 headers collapse to "+N".
function OriginalHeader({ field, query }) {
  const d = fieldHeaderDisplay(field, query);
  if (!d.showArrow) {
    return (
      <>
        <span className="s3upload-grid-dash" aria-hidden="true">—</span>
        <span className="s3upload-sr-only">Same as field name</span>
      </>
    );
  }
  return (
    <span className="s3upload-tree-orig" title={d.title}>
      {d.shown.map((h, i) => (
        <React.Fragment key={`${h}-${i}`}>{i > 0 && ", "}<Highlight text={h} query={query} /></React.Fragment>
      ))}
      {d.extra > 0 && (
        <span className="s3upload-tree-more">
          <span aria-hidden="true"> +{d.extra}</span>
          <span className="s3upload-sr-only"> and {d.extra} more ({d.title})</span>
        </span>
      )}
    </span>
  );
}

function Skeleton() {
  return (
    <div className="s3upload-grid-wrap" aria-busy="true" aria-label="Loading table structure">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="s3upload-grid-skel">
          <div className="skeleton" style={{ width: "16px", height: "16px", borderRadius: "3px", flexShrink: 0 }} />
          <div className="skeleton" style={{ width: `${34 - i * 4}%`, height: "10px" }} />
          <div className="skeleton" style={{ width: "12%", height: "10px", marginLeft: "auto" }} />
        </div>
      ))}
    </div>
  );
}

// "in 3 tables" on a shared field row; the tooltip lists the other tables and a click opens the
// Relationships view at that field.
function SharedLink({ entry, category, onShow }) {
  const others = entry.tables.filter((c) => c !== category);
  const list = others.join(", ");
  return (
    <button type="button" className="s3upload-grid-link" title={`Also in: ${list}`}
      aria-label={`In ${entry.count} tables, also in ${list}. Show in Relationships`}
      onClick={(e) => { e.stopPropagation(); onShow(entry.name); }}>
      <Link2 size={12} aria-hidden="true" />
      <span aria-hidden="true">in {entry.count} tables</span>
    </button>
  );
}

function TableRow({ table, open, query, totalFields, onToggle, onSetOpen }) {
  const cat = table.category;
  const fieldsLabel = query ? `${table.fields.length} of ${totalFields}` : String(table.fields.length);
  const fileTitle = table.files.map((f) => friendlyFileName(f.file_name)).join(", ");
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight" && !open) { e.preventDefault(); onSetOpen(cat, true); }
    if (e.key === "ArrowLeft" && open) { e.preventDefault(); onSetOpen(cat, false); }
  };
  return (
    <tr className={`s3upload-grid-row parent${open ? " open" : ""}`} onClick={() => onToggle(cat)}>
      <th scope="row" className="s3upload-grid-name">
        <span className="s3upload-grid-namecell">
          <button type="button" className="s3upload-grid-toggle" aria-expanded={open}
            aria-label={`${open ? "Collapse" : "Expand"} ${cat}`}
            onClick={(e) => { e.stopPropagation(); onToggle(cat); }} onKeyDown={onKeyDown}>
            {open ? <Minus size={12} strokeWidth={2.5} aria-hidden="true" /> : <Plus size={12} strokeWidth={2.5} aria-hidden="true" />}
          </button>
          <span className="s3upload-grid-tlabel">
            <span className="s3upload-grid-tname" title={table.description ? `${cat}: ${table.description}` : cat}>{cat}</span>
            {table.physical_table && (
              <span className="s3upload-tree-phys" title={`Physical table: ${table.physical_table}`}>{table.physical_table}</span>
            )}
          </span>
        </span>
      </th>
      <td><span className="s3upload-grid-muted">Table</span></td>
      <td />
      <td className="num">
        {fieldsLabel}
        {query && <span className="s3upload-sr-only"> fields match</span>}
      </td>
      <td className="num" title={fileTitle || undefined}>{table.file_count}</td>
      <td className="s3upload-grid-date">{fmtDate(table.last_upload)}</td>
    </tr>
  );
}

// Description, note (e.g. "fields unavailable") and newest file names for an open table.
function TableInfoRow({ table }) {
  if (!table.description && !table.note && !table.files.length) return null;
  return (
    <tr className="s3upload-grid-row info">
      <td colSpan={GRID_COLS}>
        <div className="s3upload-grid-info">
          {table.description && <span>{table.description}</span>}
          {table.note && <span className="s3upload-tree-note">{table.note}</span>}
          {table.files.length > 0 && (
            <span>
              <b>Files:</b> {table.files.map((f) => friendlyFileName(f.file_name)).join(", ")}
              {table.file_count > table.files.length && ` and ${table.file_count - table.files.length} more`}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

function FieldRow({ table, field, query, shared, onShowRelationship }) {
  return (
    <tr className="s3upload-grid-row child">
      <td className="s3upload-grid-name">
        <span className="s3upload-grid-namecell child">
          <span className="s3upload-tree-fname"><Highlight text={field.name} query={query} /></span>
          <span className="s3upload-sr-only">, field in {table.category}</span>
          {shared && <SharedLink entry={shared} category={table.category} onShow={onShowRelationship} />}
        </span>
      </td>
      <td><TypeBadge type={field.type} format={field.format} /></td>
      <td><OriginalHeader field={field} query={query} /></td>
      <td /><td /><td />
    </tr>
  );
}

function StructureGrid({ rows, query, totalFieldsByCategory, sharedIndex, onToggle, onSetOpen, onShowRelationship }) {
  return (
    <div className="s3upload-grid-wrap">
      <table className="s3upload-grid" aria-label="Tables and fields in this project">
        <thead>
          <tr>
            <th scope="col" className="s3upload-grid-col-name">Name</th>
            <th scope="col" className="s3upload-grid-col-type">Type</th>
            <th scope="col" className="s3upload-grid-col-orig">Original header</th>
            <th scope="col" className="num s3upload-grid-col-num">Fields</th>
            <th scope="col" className="num s3upload-grid-col-num">Files</th>
            <th scope="col" className="s3upload-grid-col-date">Last upload</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (r.kind === "table" ? (
            <React.Fragment key={r.key}>
              <TableRow table={r.table} open={r.open} query={query}
                totalFields={totalFieldsByCategory[r.table.category] ?? r.table.fields.length}
                onToggle={onToggle} onSetOpen={onSetOpen} />
              {r.open && !query && <TableInfoRow table={r.table} />}
            </React.Fragment>
          ) : (
            <FieldRow key={r.key} table={r.table} field={r.field} query={query}
              shared={sharedIndex.get(r.field.name)} onShowRelationship={onShowRelationship} />
          )))}
        </tbody>
      </table>
    </div>
  );
}

const COMMON_TAG = { date: "date field", all: "metric" };
const COMMON_TITLE = {
  date: "A generic date/time field: hidden by default",
  all: "A measure every table has: lets Boomerang compare tables, not a key to join them on (hidden by default)",
};
const METRIC_TITLE = "A shared measure: lets Boomerang compare or combine these tables, not a key to join them on";

function RelationshipsView({ tableCount, shared, partition, hideCommon, setHideCommon, visible, query, focusField }) {
  if (tableCount < 2) {
    return (
      <div className="s3upload-empty">
        <div className="s3upload-empty-icon"><Share2 size={22} color="#94a3b8" aria-hidden="true" /></div>
        <p>Relationships appear when two or more tables share a field name.</p>
      </div>
    );
  }
  if (!shared.length) {
    return (
      <div className="s3upload-empty">
        <div className="s3upload-empty-icon"><Share2 size={22} color="#94a3b8" aria-hidden="true" /></div>
        <p>No shared field names across these tables yet.</p>
      </div>
    );
  }
  const hiddenCount = hideCommon ? partition.common.length : 0;
  let empty = null;
  if (!visible.length) {
    empty = query
      ? <p>No shared fields match &lsquo;{query}&rsquo;.</p>
      : <p>All {plural(shared.length, "shared field is a common field", "shared fields are common fields")} (dates or metrics in every table).</p>;
  }
  return (
    <>
      <div className="s3upload-rel-tools">
        <label className="s3upload-rel-check" title="Hides dates and metrics that every table has, so the fields that actually link tables stand out.">
          <input type="checkbox" checked={hideCommon} onChange={(e) => setHideCommon(e.target.checked)} />
          Show only likely join keys
        </label>
        {hideCommon ? (
          hiddenCount > 0 && (
            <span className="s3upload-tree-muted">
              {plural(hiddenCount, "common field", "common fields")} hidden (dates and metrics in every table) ·{" "}
              <button type="button" className="s3upload-linkbtn" onClick={() => setHideCommon(false)}>Show all</button>
            </span>
          )
        ) : (
          <span className="s3upload-tree-muted">
            Showing every shared field. Turn on to hide dates and metrics present in every table.
          </span>
        )}
      </div>
      {empty ? (
        <div className="s3upload-empty">{empty}</div>
      ) : (
        <div className="s3upload-grid-wrap">
          <table className="s3upload-grid rel" aria-label="Shared fields between tables">
            <thead>
              <tr>
                <th scope="col" className="s3upload-grid-col-name">Field name</th>
                <th scope="col" className="s3upload-grid-col-type">Type</th>
                <th scope="col" className="s3upload-grid-col-shared">Shared by</th>
                <th scope="col">Tables</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((e) => {
                const reason = commonFieldReason(e, tableCount);
                return (
                  <tr key={e.name} id={relRowId(e.name)} tabIndex={-1}
                    className={`s3upload-grid-row rel${focusField === e.name ? " focus" : ""}`}>
                    <th scope="row" className="s3upload-grid-name">
                      <span className="s3upload-grid-namecell">
                        <span className="s3upload-tree-fname"><Highlight text={e.name} query={query} /></span>
                        {reason && <span className="s3upload-rel-tag" title={COMMON_TITLE[reason]}>{COMMON_TAG[reason]}</span>}
                        {!reason && isSharedMetric(e) && (
                          <span className="s3upload-rel-tag" title={METRIC_TITLE}>metric</span>
                        )}
                      </span>
                    </th>
                    <td>
                      <TypeBadge type={e.type} />
                      {e.types.length > 1 && (
                        <span className="s3upload-type-format" title={`Types across tables: ${e.types.join(", ")}`}> mixed</span>
                      )}
                    </td>
                    <td className="s3upload-rel-count">{e.count} tables</td>
                    <td>
                      <ul className="s3upload-rel-chips" aria-label={`Tables with ${e.name}`}>
                        {e.tables.map((c) => (
                          <li key={c} className="s3upload-rel-chip"><Highlight text={c} query={query} /></li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Footnotes({ data }) {
  const n = data.archived_only.length;
  const u = data.uncategorized_file_count;
  return (
    <>
      {n > 0 && (
        <p className="s3upload-tree-foot">
          {n === 1 ? "1 data set only has archived files and is hidden here." : `${n} data sets only have archived files and are hidden here.`}
        </p>
      )}
      {u > 0 && (
        <p className="s3upload-tree-foot">
          {u === 1 ? "1 file isn't in a data set yet, so it isn't shown." : `${u} files aren't in a data set yet, so they aren't shown.`}
        </p>
      )}
    </>
  );
}

function SearchBox({ value, onChange, inputRef, placeholder }) {
  const clear = () => { onChange(""); if (inputRef.current) inputRef.current.focus(); };
  return (
    <div className="s3upload-tree-searchwrap">
      <label htmlFor={SEARCH_ID} className="s3upload-sr-only">Search fields</label>
      <Search size={14} className="s3upload-tree-search-icon" aria-hidden="true" />
      <input
        id={SEARCH_ID}
        ref={inputRef}
        type="text"
        className="s3upload-tree-search"
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        value={value}
        aria-describedby={SEARCH_STATUS_ID}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape" && value) { e.preventDefault(); onChange(""); } }}
      />
      {value && (
        <button type="button" className="s3upload-tree-search-clear" aria-label="Clear field search" onClick={clear}>
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export default function TableStructureView({ idToken, projectId, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [view, setView] = useState("structure");
  const [hideCommon, setHideCommon] = useState(false);
  const [focusField, setFocusField] = useState(null);
  // Open/closed state lives here (not per row) so it survives the grid unmounting during a no-match
  // search. `openCats` = the user's own choice (default: all collapsed); while searching, tables with
  // matches open automatically and `searchCollapsed` holds the ones the user closed for this query.
  const [openCats, setOpenCats] = useState(() => new Set());
  const [searchCollapsed, setSearchCollapsed] = useState(() => new Set());
  const reqRef = useRef(0);
  const searchRef = useRef(null);
  // Field to move focus to ONCE after an "in N tables" jump; cleared as soon as focus lands so later
  // list changes (typing in search, "Hide common fields") never pull focus off the user's control.
  const pendingFocusRef = useRef(null);

  // A project switch must never show the previous project's tables (or search) while the new ones load.
  useEffect(() => {
    setData(null); setQuery(""); setOpenCats(new Set()); setFocusField(null);
    pendingFocusRef.current = null;
  }, [projectId]);

  const load = useCallback(async () => {
    if (!idToken) return;
    const req = ++reqRef.current;
    setLoading(true);
    setError(false);
    try {
      const token = (await getFreshToken()) || idToken;
      const res = await fetch(`${API_URL}/upload/tables`, {
        headers: { Authorization: `Bearer ${token}`, "X-Project-Id": projectId },
      });
      if (req !== reqRef.current) return; // a newer request (project switch / refresh) won
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      if (req !== reqRef.current) return;
      setData(normalizeTableStructure(body));
    } catch (err) {
      if (req !== reqRef.current) return;
      console.error("Failed to load table structure", err);
      setError(true);
    } finally {
      if (req === reqRef.current) setLoading(false);
    }
  }, [idToken, projectId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // The input updates immediately; the (possibly large) grid re-renders with the deferred value so
  // typing stays responsive at ~100 tables x ~50 fields.
  const deferredQuery = useDeferredValue(query);
  const tables = data ? data.tables : null;
  const summary = useMemo(() => summarizeTables(tables || []), [tables]);
  const filtered = useMemo(() => filterTableStructure(tables || [], deferredQuery), [tables, deferredQuery]);
  const totalFieldsByCategory = useMemo(() => {
    const m = {};
    (tables || []).forEach((t) => { m[t.category] = t.fields.length; });
    return m;
  }, [tables]);
  const shared = useMemo(() => computeSharedFields(tables || []), [tables]);
  const sharedIndex = useMemo(() => sharedFieldIndex(shared), [shared]);
  const tableCount = tables ? tables.length : 0;
  const partition = useMemo(() => partitionSharedFields(shared, tableCount), [shared, tableCount]);
  const relVisible = useMemo(
    () => filterSharedFields(hideCommon ? partition.useful : shared, deferredQuery),
    [hideCommon, partition, shared, deferredQuery],
  );

  const searching = Boolean(filtered.query);
  useEffect(() => { setSearchCollapsed(new Set()); }, [filtered.query]);
  const isTableOpen = useCallback(
    (cat) => (searching ? !searchCollapsed.has(cat) : openCats.has(cat)),
    [searching, searchCollapsed, openCats],
  );
  const rows = useMemo(() => buildTreeRows(filtered.tables, isTableOpen), [filtered.tables, isTableOpen]);

  const flip = (set, cat) => { const n = new Set(set); if (n.has(cat)) n.delete(cat); else n.add(cat); return n; };
  const toggleTable = (cat) => (searching
    ? setSearchCollapsed((s) => flip(s, cat))
    : setOpenCats((s) => flip(s, cat)));
  const setTableOpen = (cat, open) => { if (isTableOpen(cat) !== open) toggleTable(cat); };
  const bulk = (expand) => {
    const next = bulkToggleState(filtered.tables.map((t) => t.category), expand, searching);
    if (next.key === "openCats") setOpenCats(next.value); else setSearchCollapsed(next.value);
  };
  const showRelationship = (name) => {
    const entry = sharedIndex.get(name);
    if (entry && commonFieldReason(entry, tableCount)) setHideCommon(false);
    setFocusField(name);
    pendingFocusRef.current = name;
    setView("relationships");
  };
  const switchView = (v) => { setView(v); setFocusField(null); pendingFocusRef.current = null; };

  // After "in N tables" is clicked: scroll the Relationships row into view and move focus to it,
  // exactly once. `relVisible` stays a dependency only so the jump still lands if the row appears a
  // render later (after un-hiding common fields); the highlight (`focusField`) persists.
  useEffect(() => {
    const pending = pendingFocusRef.current;
    if (view !== "relationships" || !pending) return;
    const el = document.getElementById(relRowId(pending));
    if (!el) return;
    pendingFocusRef.current = null;
    if (el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
    el.focus({ preventScroll: true });
  }, [view, focusField, relVisible]);

  const typeText = typeSummaryText(summary);
  const hasTables = Boolean(data && !error && data.tables.length > 0);
  const relView = view === "relationships";

  let status = "";
  if (hasTables && deferredQuery.trim()) {
    if (relView) {
      status = relVisible.length === 0
        ? `No shared fields match '${deferredQuery.trim()}'`
        : `${plural(relVisible.length, "shared field", "shared fields")} ${relVisible.length === 1 ? "matches" : "match"}`;
    } else {
      status = filtered.fieldCount === 0
        ? `No fields match '${deferredQuery.trim()}'`
        : `${plural(filtered.fieldCount, "field", "fields")} in ${plural(filtered.tableCount, "table", "tables")} `
          + (filtered.fieldCount === 1 ? "matches" : "match");
    }
  }

  let body;
  if (error) {
    body = (
      <div className="s3upload-empty" role="alert">
        <p>We couldn't load the table structure. Please try again.</p>
        <button type="button" className="s3upload-btn refresh" onClick={load} style={{ marginTop: "10px" }}>Try again</button>
      </div>
    );
  } else if (!data) {
    body = <Skeleton />;
  } else if (data.tables.length === 0) {
    body = (
      <div className="s3upload-empty">
        <div className="s3upload-empty-icon"><Table2 size={22} color="#94a3b8" aria-hidden="true" /></div>
        <p>No tables yet. Upload a file to see its structure.</p>
        <Footnotes data={data} />
      </div>
    );
  } else if (relView) {
    body = (
      <>
        <RelationshipsView tableCount={tableCount} shared={shared} partition={partition}
          hideCommon={hideCommon} setHideCommon={setHideCommon} visible={relVisible}
          query={deferredQuery.trim()} focusField={focusField} />
        {tableCount >= 2 && shared.length > 0 && relVisible.length > 0 && (
          <p className="s3upload-grid-foot">{plural(relVisible.length, "shared field", "shared fields")} shown</p>
        )}
      </>
    );
  } else if (searching && filtered.fieldCount === 0) {
    body = (
      <>
        <div className="s3upload-empty">
          <div className="s3upload-empty-icon"><Search size={22} color="#94a3b8" aria-hidden="true" /></div>
          <p>Try another field name or a column header from your file.</p>
          <button type="button" className="s3upload-btn refresh" style={{ margin: "10px auto 0" }}
            onClick={() => { setQuery(""); if (searchRef.current) searchRef.current.focus(); }}>
            Clear search
          </button>
        </div>
        <Footnotes data={data} />
      </>
    );
  } else {
    body = (
      <>
        <StructureGrid rows={rows} query={filtered.query} totalFieldsByCategory={totalFieldsByCategory}
          sharedIndex={sharedIndex} onToggle={toggleTable} onSetOpen={setTableOpen}
          onShowRelationship={showRelationship} />
        <p className="s3upload-grid-foot">
          {plural(filtered.tableCount, "table", "tables")} · {plural(filtered.fieldCount, "field", "fields")} shown
        </p>
        <Footnotes data={data} />
      </>
    );
  }

  const relCount = hideCommon ? partition.useful.length : shared.length;
  return (
    <div className="s3upload-tables-view">
      <p className="s3upload-tables-bar" aria-live="polite">
        {hasTables ? (
          <>
            <span className="s3upload-tables-summary">{summaryText(summary)}</span>
            {typeText && <span className="s3upload-tables-types">{typeText}</span>}
          </>
        ) : (
          <span className="s3upload-tree-muted">
            {data && !error ? `${plural(data.tables.length, "table", "tables")} in this project` : "Table structure"}
          </span>
        )}
        {loading && data && <span className="s3upload-tree-muted"> · refreshing…</span>}
      </p>
      {hasTables && (
        <div className="s3upload-tree-searchbar">
          <div className="s3upload-seg" role="group" aria-label="Tables view">
            <button type="button" className={`s3upload-seg-btn${!relView ? " on" : ""}`} aria-pressed={!relView}
              onClick={() => switchView("structure")}>
              Structure
            </button>
            <button type="button" className={`s3upload-seg-btn${relView ? " on" : ""}`} aria-pressed={relView}
              onClick={() => switchView("relationships")}>
              Relationships
              {relCount > 0 && <span className="s3upload-seg-count">{relCount}</span>}
            </button>
          </div>
          <SearchBox value={query} onChange={setQuery} inputRef={searchRef}
            placeholder={relView ? "Search shared fields or tables" : "Search fields or original headers"} />
          <p id={SEARCH_STATUS_ID} className="s3upload-tree-search-status" role="status">{status}</p>
          {!relView && (
            <div className="s3upload-grid-bulk">
              <button type="button" className="s3upload-grid-bulkbtn" onClick={() => bulk(true)}>
                <ChevronsUpDown size={13} aria-hidden="true" /> Expand all
              </button>
              <button type="button" className="s3upload-grid-bulkbtn" onClick={() => bulk(false)}>
                <ChevronsDownUp size={13} aria-hidden="true" /> Collapse all
              </button>
            </div>
          )}
        </div>
      )}
      {relView && hasTables && <p className="s3upload-rel-intro">{REL_INTRO}</p>}
      {body}
    </div>
  );
}
